import type React from 'react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  RiFacebookFill,
  RiInstagramLine,
  RiTwitterXFill,
  RiWhatsappFill,
} from 'react-icons/ri';

import 'react-toastify/dist/ReactToastify.css';

import { CompetitionType } from '../InviteFriendsPage';

import { Button, InputField } from '@/components';
import { IconGmail } from '@/components/Icons/IconGmail';
import { IconOutlook } from '@/components/Icons/IconOutlook';
import useFriendsCup from '@/hooks/useFriendsCup';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useMinileagueData } from '@/hooks/useMinileagueData';
import useToast from '@/hooks/useToast';
import useTourney from '@/hooks/useTourney';
import { isAndroid } from '@/utils'; // Asumo que isAndroid() sigue siendo útil

interface InviteByLinkProps {
  idTorneo: string;
  type: 'tournament' | 'cup' | string;
  isDesktop?: boolean;
}

type SocialIconProps = {
  icon: React.ReactNode;
  onClick: () => void;
  classIcon?: string;
};

export const SocialMediaKeys = {
  whatsapp: 'whatsapp',
  facebook: 'facebook',
  twitter: 'twitter',
  gmail: 'gmail',
  outlook: 'outlook',
  instagram: 'instagram',
} as const;

export type SocialMediaType = keyof typeof SocialMediaKeys;

const SocialIcon: React.FC<SocialIconProps> = ({
  icon,
  onClick,
  classIcon,
}) => (
  <button
    className="min-w-11 min-h-11 lg:w-14 lg:h-14 rounded-xl bg-white shadow-sm flex items-center justify-center hover:bg-gray-50"
    onClick={onClick}
  >
    <figure className={classIcon}>{icon}</figure>
  </button>
);

// TODO: Implementar la interfaz de compartición para Android si es necesario
// class JavaScriptShareInterface(private val context: Context) {
//     @JavascriptInterface
//     fun share(title: String, text: String, url: String) {
//         val sharingIntent = Intent(Intent.ACTION_SEND).apply {
//             type = "text/plain"
//             putExtra(Intent.EXTRA_SUBJECT, title)
//             putExtra(Intent.EXTRA_TEXT, "$text $url")
//         }
//         context.startActivity(Intent.createChooser(sharingIntent, "Share via"))
//     }
// }
// TODO: Configurar el WebView en Android para usar la interfaz de compartición
// val webView: WebView = findViewById(R.id.my_webview)
// webView.settings.javaScriptEnabled = true // Make sure JavaScript is enabled
// webView.addJavascriptInterface(JavaScriptShareInterface(this), "AndroidShareHandler")

export const InviteByLink: React.FC<InviteByLinkProps> = ({
  idTorneo,
  type = 'tournament',
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const [linkToShare, setLinkToShare] = useState('');

  const [competitionInfo, setCompetitionInfo] = useState({
    tipo: 'Torneo de Amigos',
    nombre: '',
    textoFinal: '',
  });
  const toast = useToast();
  const { tourneyData } = useTourney();
  const { groupData } = useFriendsCup();
  const { miniligueData } = useMinileagueData();
  const { isDesktop, isMobile } = useIsDesktop();

  const messageToShare = useMemo(
    () =>
      `¡Te invito a participar de mi ${competitionInfo.tipo} ${competitionInfo.nombre} en ${competitionInfo.textoFinal} Gran DT. Postulate acá!`, // El link se pasa por separado a la API nativa
    [competitionInfo]
  );

  // Genera el link a compartir, esto se mantuvo igual.
  useEffect(() => {
    const baseUrl = window.location.origin;

    let path = '';
    if (path === '')
      if (type === CompetitionType.CUP) {
        path = `/copa-de-amigos/invitar/${idTorneo}`;
        setCompetitionInfo({
          tipo: 'Equipo',
          nombre: groupData?.nombre || 'Mi Equipo',
          textoFinal: 'la Copa de Amigos de',
        });
      } else if (type === CompetitionType.TOURNAMENT) {
        path = `/torneo-de-amigos/invitar/${idTorneo}`;
        setCompetitionInfo({
          tipo: 'Torneo de Amigos',
          nombre: tourneyData?.tourney?.nombre || 'Mi Torneo',
          textoFinal: '',
        });
      } else if (type === CompetitionType.MINILEAGUES) {
        path = `/miniligas/invitar/${idTorneo}`;
        setCompetitionInfo({
          tipo: 'Miniliga',
          nombre: miniligueData.miniLiga.nombreML || 'Mi Miniliga',
          textoFinal: '',
        });
      }

    setLinkToShare(`${baseUrl}${path}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idTorneo, type, groupData, tourneyData, miniligueData]);

  const handleCopyLink = useCallback(async () => {
    try {
      // Copiamos el mensaje completo con el link
      await navigator.clipboard.writeText(`${messageToShare} ${linkToShare}`);
      setIsCopied(true);
      toast.show('Enlace copiado.');
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Error copiando en el portapapeles:', err);
      toast.show('Error al copiar el enlace.');
    }
  }, [linkToShare, messageToShare, toast]);

  /**
   * Maneja la compartición utilizando la API de Web Share (nativo)
   * o esquemas de URL/popups como fallback.
   * @param platform La plataforma social a la que se desea compartir.
   */
  const handleShare = useCallback(
    async (platform?: SocialMediaType) => {
      const fullMessage = `${messageToShare} ${linkToShare}`;
      const encodedMessage = encodeURIComponent(fullMessage);
      const titleBody = encodeURIComponent(
        'Gran DT\nEl Juego que apasiona a millones.\n\n\n'
      );

      // 1. Intenta usar la Web Share API (para móvil es lo mejor)
      if (navigator.share && isMobile) {
        try {
          await navigator.share({
            title: 'Invitación Gran DT',
            text: messageToShare,
            url: linkToShare, // Para la mayoría de las apps (ej. WhatsApp, Telegram)
          });
        } catch (error) {
          // La API falló o el usuario la descartó. Intentamos el fallback específico.
          console.error('Web Share API falló o fue descartada.', error);
        }

        return;
      }

      let shareUrl = '';

      switch (platform) {
        case SocialMediaKeys.whatsapp: {
          // Lógica de WhatsApp: intent en Android, URL en otros
          if (isAndroid()) {
            // Intent de Android (más robusto para abrir la app)
            shareUrl = `intent://send?text=${encodedMessage}#Intent;scheme=whatsapp;package=com.whatsapp;end`;
          } else {
            // Para navegadores normales y iOS
            shareUrl = `https://wa.me/?text=${encodedMessage}`;
          }
          break;
        }
        case SocialMediaKeys.facebook:
          shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            linkToShare
          )}`;
          break;
        case SocialMediaKeys.twitter:
          // Twitter/X prefiere el mensaje completo, no solo el link
          shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
            messageToShare
          )}&url=${encodeURIComponent(linkToShare)}`;
          break;
        case SocialMediaKeys.gmail:
          // Se usa la vista de composición de Gmail
          shareUrl = `https://mail.google.com/mail/?view=cm&su=${encodeURIComponent(
            '¡Te invito a participar de mi '
          )}${competitionInfo.tipo}!&body=${titleBody}${encodedMessage}`;
          break;
        case SocialMediaKeys.outlook:
          // Mailto genérico, con enlace al cliente de Outlook si es posible
          shareUrl = `mailto:?subject=${encodeURIComponent(
            '¡Te invito a participar de mi '
          )}${competitionInfo.tipo}!&body=${titleBody}${encodedMessage}`;
          break;
        case SocialMediaKeys.instagram:
          // Instagram no tiene una API de compartición por URL para Stories/DM
          // Se mantiene la lógica de copiar al portapapeles e instruir al usuario.
          await navigator.clipboard.writeText(fullMessage);
          toast.show(
            <div>
              ¡Link copiado! Ingresá a Instagram y compartilo.{' '}
              <a
                className="underline"
                href="https://www.instagram.com/"
                rel="noreferrer"
                target="_blank"
              >
                Accedé desde acá.
              </a>
              .
            </div>
          );
          return;
        default:
          return; // Salir aquí porque ya se manejó la acción
      }

      // Abre la URL de compartición si se generó una (fallback)
      if (shareUrl) window.open(shareUrl, '_blank');
    },
    [competitionInfo.tipo, isMobile, linkToShare, messageToShare, toast]
  );

  const activationName = useMemo(() => {
    if (type === CompetitionType.CUP) return 'equipo';
    if (type === CompetitionType.TOURNAMENT) return 'torneo';
    if (type === CompetitionType.MINILEAGUES) return 'miniliga';
    return '';
  }, [type]);

  const ContentDesktop = useCallback(() => {
    return (
      <>
        <div>
          <p className="text-[#333333] text-base font-semibold mb-4">
            Compartí el link de tu {activationName}
          </p>

          <div className="flex items-center justify-start">
            <div className="w-[320px]">
              <InputField
                readOnly
                behavior="default"
                name="link"
                placeholder="Compartir link"
                value={linkToShare}
                onChange={() => {
                  /* No-op */
                }}
              />
            </div>

            <Button
              className="!bg-[#e3eaff] !text-[#3366ff] ml-4"
              type="button"
              variant="secondary"
              onClick={handleCopyLink}
            >
              {isCopied ? 'Copiado' : 'Copiar link'}
            </Button>
          </div>
        </div>

        <div className="pt-4">
          <p className="text-[#333333] text-base font-semibold mb-4">
            Compartir por redes sociales
          </p>
          <div className="flex items-center gap-4 flex-wrap">
            <SocialIcon
              icon={<RiFacebookFill className="text-[#1877F2]" size={28} />}
              onClick={() => handleShare(SocialMediaKeys.facebook)}
            />
            <SocialIcon
              icon={<RiWhatsappFill className="text-[#25D366]" size={28} />}
              onClick={() => handleShare(SocialMediaKeys.whatsapp)}
            />
            <SocialIcon
              icon={<RiInstagramLine className="text-[#E4405F]" size={32} />}
              onClick={() => handleShare(SocialMediaKeys.instagram)}
            />
            <SocialIcon
              classIcon="w-7"
              icon={<IconGmail />}
              onClick={() => handleShare(SocialMediaKeys.gmail)}
            />
            <SocialIcon
              icon={<RiTwitterXFill size={23} />}
              onClick={() => handleShare(SocialMediaKeys.twitter)}
            />
            <SocialIcon
              classIcon="w-7"
              icon={<IconOutlook />}
              onClick={() => handleShare(SocialMediaKeys.outlook)}
            />
          </div>
        </div>
      </>
    );
  }, [activationName, handleCopyLink, handleShare, isCopied, linkToShare]);

  const ContentMobile = useCallback(() => {
    return (
      <div>
        <p className="text-[#333333] text-base font-semibold mb-4">
          Copiá o compartí el link de tu {activationName}
        </p>

        <div className="flex items-center justify-center space-x-4">
          <Button
            className="flex-1"
            type="button"
            variant="secondary"
            onClick={handleCopyLink}
          >
            {isCopied ? 'Copiado' : 'Copiar link'}
          </Button>
          <Button
            className="flex-1"
            type="button"
            variant="secondary"
            onClick={handleShare}
          >
            Compartir
          </Button>
        </div>
      </div>
    );
  }, [activationName, handleCopyLink, handleShare, isCopied]);

  return (
    <div className="space-y-6 md:space-y-4">
      {/* ... (Resto del JSX se mantuvo igual) */}
      {type === 'cup' && (
        <p
          className={`${
            !isDesktop
              ? 'text-[#333333] text-base leading-tight mb-6'
              : 'text-md font-medium text-[#333333] mb-6'
          }`}
        >
          Recordá que el equipo suma con los puntos de tu 11 y el de tus 3
          amigos. Si no está completo, no sumará puntos.
        </p>
      )}

      {isDesktop && <ContentDesktop />}
      {isMobile && <ContentMobile />}
    </div>
  );
};
